export const isStandaloneDisplay = (): boolean => {
  if (typeof window === 'undefined') return false;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return window.matchMedia('(display-mode: standalone)').matches || Boolean(nav.standalone);
};

export const isIosDevice = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

/**
 * Only Safari turns Add to Home Screen into a standalone app. Chrome, Firefox and Edge on iOS
 * render through WebKit but create a plain bookmark, so the install steps do not apply to them.
 */
export const isIosSafari = (): boolean =>
  isIosDevice() && !/crios|fxios|edgios|opios/i.test(navigator.userAgent);

export const supportsAppBadge = (): boolean =>
  typeof navigator !== 'undefined' &&
  typeof (navigator as Navigator & { setAppBadge?: unknown }).setAppBadge === 'function';

export type BadgePermissionState =
  | 'unsupported'
  | 'needs-install'
  | 'default'
  | 'granted'
  | 'denied';

/** iOS only paints the Home Screen badge once notifications are allowed for the installed app. */
export const getBadgePermissionState = (): BadgePermissionState => {
  if (isIosDevice() && !isStandaloneDisplay()) return 'needs-install';
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission as BadgePermissionState;
};

export const requestBadgePermission = async (): Promise<BadgePermissionState> => {
  if (typeof Notification === 'undefined') return 'unsupported';
  try {
    const result = await Notification.requestPermission();
    return result as BadgePermissionState;
  } catch (err) {
    console.warn('Notification permission request failed:', err);
    return 'denied';
  }
};

export const syncAppBadge = (count: number): void => {
  const nav = navigator as Navigator & {
    setAppBadge?: (n: number) => Promise<void>;
    clearAppBadge?: () => Promise<void>;
  };
  if (typeof nav.setAppBadge !== 'function') return;
  const done =
    count > 0
      ? nav.setAppBadge(count)
      : typeof nav.clearAppBadge === 'function'
        ? nav.clearAppBadge()
        : null;
  if (done) {
    void done.catch(() => {
      /* badge blocked (no notification permission yet) */
    });
  }
};

export const registerServiceWorker = (): void => {
  if (process.env.NODE_ENV !== 'production') return;
  if (!('serviceWorker' in navigator)) return;

  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch((err) => {
      console.warn('Service worker registration failed:', err);
    });
  });
};
